<template>
  <b-row>
    <b-col cols="12">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h3 class="mb-0 font-weight-bolder">Game Promotions</h3>
        <b-link to="/promotions/game/new">
          <b-button variant="dark" class="d-flex align-items-center">
            <feather-icon icon="PlusIcon" class="mr-1" />
            New Game
          </b-button>
        </b-link>
      </div>
    </b-col>

    <b-col cols="12">
      <b-card>
        <!-- Table -->
        <b-table
          :items="items"
          :fields="fields"
          responsive
          :busy="loading"
          show-empty
          :key="tableKey"
          thead-class="text-nowrap"
        >
          <template #empty>
            <div class="text-center text-dark my-2">
              No matching records found
            </div>
          </template>
          <template #cell(created)="{ item }">
            <span>{{ format(new Date(item.startTime), "yyyy-MM-dd") }}</span>
          </template>
          <template #cell(expiry)="{ item }">
            <span class="text-nowrap">{{
              format(new Date(item.endTime), "yyyy-MM-dd")
            }}</span>
          </template>
          <template #cell(refCode)="{ item }">
            <span>{{ item.code }}</span>
          </template>
          <template #cell(market)="{ item }">
            <span>{{ item.market || "-" }}</span>
          </template>
          <template #cell(pricing)="{ item }">
            <span>{{ item.pricing || "-" }}</span>
          </template>
          <template #cell(segments)="{ item }">
            <span
              class="text-nowrap"
              v-for="(target, idx) in item.targets"
              :key="target.id || idx"
              >{{
                target.targetType +
                (target.targetType !== "CustomerSegment" &&
                target.targetType !== "UserList"
                  ? "-" + target.targetId
                  : "")
              }}{{ idx < item.targets.length - 1 ? ", " : "" }}</span
            >
          </template>
          <template #cell(customers)="{ item }">
            <span>{{ item.customers || "-" }}</span>
          </template>
          <template #cell(maxExposure)="{ item }">
            <span>{{ item.globalExposureLimit || "-" }}</span>
          </template>

          <template #cell(actions)="{ item }">
            <div class="d-flex">
              <b-button
                size="sm"
                variant="outline-primary"
                class="mr-1"
                @click="editPromotion(item)"
              >
                <feather-icon icon="EditIcon" />
              </b-button>
              <b-button
                size="sm"
                variant="outline-danger"
                @click="openDeleteModal(item)"
              >
                <feather-icon icon="TrashIcon" />
              </b-button>
            </div>
          </template>
        </b-table>

        <div class="d-flex flex-wrap justify-content-end align-items-center">
          <b-pagination
            v-model="page"
            :total-rows="pagination.total"
            :per-page="10"
            first-number
            last-number
          />
        </div>
      </b-card>
    </b-col>

    <b-modal
      id="delete-modal"
      ref="deletePricePromotion"
      title="Confirm Delete Price Promotion"
      centered
      hide-footer
      header-bg-variant="danger"
    >
      <div class="text-center text-body" v-if="selectedPromotion">
        <p class="mb-2 mt-1">
          Are you sure you want to delete the promotion
          <strong>{{ selectedPromotion.name }}</strong> ?
        </p>

        <div class="d-flex justify-content-end">
          <b-button variant="secondary" class="mr-1" @click="closeDeleteModal">
            Cancel
          </b-button>
          <b-button
            variant="danger"
            @click="confirmDelete"
            :disabled="loadingDelete"
          >
            Delete
          </b-button>
        </div>
      </div>
    </b-modal>
  </b-row>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BButton,
  BFormInput,
  BTable,
  BBadge,
  BPagination,
  BLink,
  BModal,
} from "bootstrap-vue";
import { mapState, mapActions } from "vuex";
import { format } from "date-fns";
import { debounce } from "debounce";

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BFormInput,
    BTable,
    BBadge,
    BPagination,
    BLink,
    BModal,
  },
  data() {
    return {
      search: "",
      page: 1,
      tableKey: 0,
      items: [],
      fields: [
        { key: "created", label: "Date Created" },
        { key: "name", label: "Name" },
        { key: "subType", label: "Description" },
        // { key: "name", label: "Promotion Name" },
        // { key: "refCode", label: "Ref Code" },
        // { key: "market", label: "Market" },
        // { key: "pricing", label: "Pricing" },
        // { key: "maxExposure", label: "Max Exposure" },
        // { key: "customers", label: "# Customers" },
        { key: "segments", label: "Segments" },
        { key: "actions", label: "Actions" },
      ],
      selectedPromotion: null,
    };
  },
  async created() {
    await this.fetchPricePromotions();
  },
  computed: {
    ...mapState("promotions/price-promotions", {
      loading: "loading",
      loadingDelete: "loadingDelete",
      pricePromotions: "pricePromotions",
      pagination: "pagination",
    }),
  },
  watch: {
    page() {
      this.fetchPricePromotions();
    },
    search() {
      this.handleSearchDebounced();
    },
  },
  mounted() {
    this.handleSearchDebounced = debounce(this.handleSearch, 300);
  },
  methods: {
    ...mapActions("promotions/price-promotions", [
      "getPricePromotions",
      "updatePricePromotion",
      "deletePricePromotion",
    ]),
    format,
    editPromotion(item) {
      this.$router.push(`/promotions/game/${item.id}`);
    },
    async fetchPricePromotions() {
      if (this.loading) return;
      try {
        const { page, search } = this;
        const params = {
          page,
          type: "Game",
          ...(search && { search }),
        };

        const data = await this.getPricePromotions(params);
        this.items = data;
      } catch (e) {
        this.items = [];
      }
    },
    handleSearch() {
      this.page = 1;
      this.fetchPricePromotions();
    },
    openDeleteModal(item) {
      this.selectedPromotion = item;
      this.$refs.deletePricePromotion.show();
    },
    closeDeleteModal() {
      this.$refs.deletePricePromotion.hide();
      this.selectedPromotion = null;
    },
    async confirmDelete() {
      const res = await this.deletePricePromotion(this.selectedPromotion.id);
      if (!res) return;
      this.closeDeleteModal();
      this.fetchPricePromotions();
    },
  },
};
</script>
